import type { CandleBar } from './barSource';

/** OHLC bar OpenAlgo accepts. Time is UTC seconds. */
export interface OpenAlgoBar {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

const OPENALGO_INTERVALS = ['1m', '5m', '15m', '30m', '60m', '4h', '1d', '1w', '1mo'] as const;

export { OPENALGO_INTERVALS };

interface DeskInterval {
  unit: 'minute' | 'hour' | 'day' | 'week' | 'month';
  count: number;
}

function finitePrice(value: number): boolean {
  return Number.isFinite(value);
}

function utcSeconds(time: number): number {
  return time > 10_000_000_000 ? Math.floor(time / 1000) : Math.floor(time);
}

/**
 * Bars for `series.setData`. Drops non-finite prices, sorts by time, and
 * keeps the last print when two bars share a timestamp.
 */
export function toOpenAlgoBars(bars: readonly CandleBar[]): OpenAlgoBar[] {
  const sorted = bars.filter((bar) => (
    finitePrice(bar.time)
    && finitePrice(bar.open)
    && finitePrice(bar.high)
    && finitePrice(bar.low)
    && finitePrice(bar.close)
  ));
  sorted.sort((a, b) => a.time - b.time);
  const out: OpenAlgoBar[] = [];
  for (const bar of sorted) {
    const next: OpenAlgoBar = {
      time: utcSeconds(bar.time),
      open: bar.open,
      high: bar.high,
      low: bar.low,
      close: bar.close,
    };
    if (Number.isFinite(bar.volume)) next.volume = bar.volume;
    const prev = out[out.length - 1];
    if (prev && prev.time === next.time) out[out.length - 1] = next;
    else out.push(next);
  }
  return out;
}

/**
 * The newest bar when `next` is the same series with only the tail replaced
 * or one bar appended. Anything else returns null so the caller uses `setData`.
 */
export function tailUpdate(
  prev: readonly OpenAlgoBar[],
  next: readonly OpenAlgoBar[],
): OpenAlgoBar | null {
  if (next.length === 0 || prev.length === 0) return null;
  if (next.length !== prev.length && next.length !== prev.length + 1) return null;
  const shared = Math.min(prev.length, next.length) - 1;
  for (let i = 0; i < shared; i += 1) {
    if (prev[i].time !== next[i].time) return null;
  }
  if (next.length === prev.length + 1 && next[next.length - 2].time !== prev[prev.length - 1].time) {
    return null;
  }
  return next[next.length - 1];
}

/** Desk interval for a widget code. `mo` is matched before `m` so `1mo` is a month. */
export function fromOpenAlgoInterval(code: string): DeskInterval | null {
  const trimmed = code.trim();
  if (/^d$/i.test(trimmed)) return { unit: 'day', count: 1 };
  if (/^w$/i.test(trimmed)) return { unit: 'week', count: 1 };
  const match = /^(\d+)(mo|m|h|d|w)$/i.exec(trimmed);
  if (!match) return null;
  const count = Number(match[1]);
  if (!Number.isInteger(count) || count < 1) return null;
  switch (match[2].toLowerCase()) {
    case 'm':
      return { unit: 'minute', count };
    case 'h':
      return { unit: 'hour', count };
    case 'd':
      return { unit: 'day', count };
    case 'w':
      return { unit: 'week', count };
    case 'mo':
      return { unit: 'month', count };
    default:
      return null;
  }
}
